import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  GroupedBillingCandidateGroup,
  GroupedBillingCandidates,
  GroupedBillingCreated,
  GroupedBillingFormOptions,
} from "@cx/contracts";
import { formatBRL, formatDateBR, formatDocument, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import {
  emptyGroupedForm,
  groupedBody,
  groupedFormProblem,
  selectedTotal,
  sharedTerm,
  type GroupedForm,
} from "./labels";

type Selection = { contactId: string; ids: Set<string> } | null;

/**
 * Faturar pedidos (RF-FIN-03): pedidos atendidos sem NF-e, por cliente; marque 2 ou mais do mesmo cliente, escolha o
 * que gerar (NF-e e/ou contas a receber), condição e forma de pagamento e a natureza da nota.
 */
export function GroupedBillingNewPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const today = todayInSaoPaulo();
  const canInvoice = can(perms, "invoices", "create");
  const canReceivables = can(perms, "receivables", "create");
  const [q, setQ] = useState("");
  const term = q.trim();
  const [sel, setSel] = useState<Selection>(null);
  const [form, setForm] = useState<GroupedForm>(() =>
    emptyGroupedForm(today, { invoice: canInvoice, receivables: canReceivables }),
  );
  const candidates = useQuery({
    queryKey: ["grouped-billing", companyId, "candidates", term],
    refetchOnMount: "always",
    queryFn: () =>
      api<GroupedBillingCandidates>(
        `/grouped-billing/candidates?${new URLSearchParams(term ? { q: term } : {})}`,
      ),
  });
  const options = useQuery({
    queryKey: ["grouped-billing", companyId, "form-options"],
    queryFn: () => api<GroupedBillingFormOptions>("/grouped-billing/form-options"),
  });
  const group: GroupedBillingCandidateGroup | undefined = sel
    ? candidates.data?.data.find((g) => g.contact.id === sel.contactId)
    : undefined;
  const ids = sel?.ids ?? new Set<string>();
  const orders = group?.orders ?? [];
  const problem = groupedFormProblem(orders, ids, form, today);
  const set = <K extends keyof GroupedForm>(k: K, v: GroupedForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const toggle = (g: GroupedBillingCandidateGroup, id: string) => {
    const next = new Set(sel?.contactId === g.contact.id ? sel.ids : []);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSel(next.size ? { contactId: g.contact.id, ids: next } : null);
    setForm((f) => ({ ...f, paymentTermId: sharedTerm(g.orders, next) }));
  };
  const toggleAll = (g: GroupedBillingCandidateGroup) => {
    const all = sel?.contactId === g.contact.id && sel.ids.size === g.orders.length;
    const next = new Set(all ? [] : g.orders.map((o) => o.id));
    setSel(next.size ? { contactId: g.contact.id, ids: next } : null);
    setForm((f) => ({ ...f, paymentTermId: sharedTerm(g.orders, next) }));
  };

  const save = useMutation({
    mutationFn: () =>
      api<GroupedBillingCreated>("/grouped-billing", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(
          groupedBody(
            sel!.contactId,
            orders.filter((o) => ids.has(o.id)).map((o) => o.id),
            form,
          ),
        ),
      }),
    onSuccess: (r) => {
      toast(
        "success",
        r.fiscalDocumentId
          ? `Faturamento agrupado nº ${r.number} criado. NF-e enviada à SEFAZ.`
          : `Faturamento agrupado nº ${r.number} criado.`,
      );
      qc.invalidateQueries({ queryKey: ["grouped-billing", companyId] });
      qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
      navigate({ to: "/financas/faturamento-agrupado/$id", params: { id: r.id } });
    },
    onError: (e: Error) => {
      toast("danger", errorMessage(e));
      qc.invalidateQueries({ queryKey: ["grouped-billing", companyId, "candidates"] });
    },
  });

  return (
    <>
      <ContentHeader
        title="Faturar pedidos"
        breadcrumb={["Finanças", "Faturamento agrupado", "Novo"]}
        actions={
          <Link to="/financas/faturamento-agrupado" className="btn btn-outline-secondary">
            Voltar
          </Link>
        }
      />
      <div className="row">
        <div className="col-xl-8">
          <div className="box card p-3 mb-3">
            <label className="form-label" htmlFor="gb-client">
              Cliente
            </label>
            <input
              id="gb-client"
              type="search"
              className="form-control"
              placeholder="Nome ou CPF/CNPJ do cliente"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="form-text">
              Aparecem os pedidos atendidos, com valor, sem NF-e própria e fora de outro faturamento.
            </div>
          </div>
          <QueryStatus loading={candidates.isLoading} error={candidates.error} />
          {candidates.isSuccess && candidates.data.data.length === 0 && (
            <div className="box card">
              <EmptyState
                title="Nenhum pedido para faturar"
                description="Os pedidos entram aqui quando ficam “Atendido” e ainda não têm NF-e."
                actions={[]}
              />
            </div>
          )}
          {candidates.data?.truncated && (
            <div className="alert alert-warning">
              Há mais pedidos do que a lista mostra: pesquise pelo cliente.
            </div>
          )}
          {candidates.data?.data.map((g) => {
            const mine = sel?.contactId === g.contact.id;
            return (
              <div key={g.contact.id} className="box card mb-3" data-testid="gb-client-group">
                <div className="d-flex flex-wrap align-items-center gap-2 p-3 pb-2">
                  <h2 className="h6 mb-0">{g.contact.name}</h2>
                  {g.contact.document && (
                    <span className="text-muted small">{formatDocument(g.contact.document)}</span>
                  )}
                  <span className="ms-auto small">
                    {g.orders.length} {g.orders.length === 1 ? "pedido" : "pedidos"} ·{" "}
                    {formatBRL(g.totalCents)}
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table align-middle mb-0" aria-label={`Pedidos de ${g.contact.name}`}>
                    <thead>
                      <tr>
                        <th scope="col" style={{ width: "2.5rem" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            aria-label={`Marcar todos os pedidos de ${g.contact.name}`}
                            checked={mine && sel.ids.size === g.orders.length}
                            onChange={() => toggleAll(g)}
                          />
                        </th>
                        <th scope="col">Pedido</th>
                        <th scope="col">Data</th>
                        <th scope="col">Vendedor</th>
                        <th scope="col">Condição</th>
                        <th scope="col">Contas</th>
                        <th scope="col" className="text-end">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.orders.map((o) => (
                        <tr key={o.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Marcar o pedido nº ${o.number}`}
                              checked={mine && sel.ids.has(o.id)}
                              onChange={() => toggle(g, o.id)}
                            />
                          </td>
                          <td>
                            <Link to="/vendas/pedidos/$id" params={{ id: o.id }}>
                              {o.number}
                            </Link>{" "}
                            <StatusBadge tone={o.status.color}>{o.status.label}</StatusBadge>
                          </td>
                          <td>{formatDateBR(o.date)}</td>
                          <td>{o.sellerName ?? "—"}</td>
                          <td>{o.paymentTermName ?? "À vista"}</td>
                          <td>
                            {o.receivablesSettled ? (
                              <StatusBadge tone="warning">Com baixa</StatusBadge>
                            ) : o.receivablesPosted ? (
                              "Lançadas"
                            ) : (
                              "Não lançadas"
                            )}
                          </td>
                          <td className="text-end">{formatBRL(o.totalCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
        <aside className="col-xl-4">
          <div className="box card p-3 mb-3">
            <h2 className="h6">Faturamento</h2>
            {group ? (
              <p className="mb-2">
                <strong>{group.contact.name}</strong>
                <br />
                <span data-testid="gb-selected">
                  {ids.size} {ids.size === 1 ? "pedido" : "pedidos"} · {formatBRL(selectedTotal(orders, ids))}
                </span>
              </p>
            ) : (
              <p className="text-muted small">Marque os pedidos de um cliente.</p>
            )}
            <fieldset className="mb-3">
              <legend className="form-label fs-6">Gerar</legend>
              <div className="form-check">
                <input
                  id="gb-nfe"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.issueInvoice}
                  disabled={!canInvoice}
                  onChange={(e) => set("issueInvoice", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="gb-nfe">
                  Uma NF-e com os itens de todos os pedidos
                </label>
              </div>
              <div className="form-check">
                <input
                  id="gb-receivables"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.postReceivables}
                  disabled={!canReceivables}
                  onChange={(e) => set("postReceivables", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="gb-receivables">
                  Contas a receber sobre o total (substituem as dos pedidos)
                </label>
              </div>
            </fieldset>
            <div className="row">
              <Field label="Data" className="col-12" hint="Base dos vencimentos das parcelas.">
                <input
                  type="date"
                  value={form.date}
                  max={today}
                  onChange={(e) => set("date", e.target.value)}
                />
              </Field>
              <Field label="Condição de pagamento" className="col-12">
                <select
                  className="form-select"
                  value={form.paymentTermId}
                  onChange={(e) => set("paymentTermId", e.target.value)}
                >
                  <option value="">À vista (uma parcela na data)</option>
                  {options.data?.paymentTerms.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Forma de pagamento" className="col-12">
                <select
                  className="form-select"
                  value={form.paymentMethodId}
                  onChange={(e) => set("paymentMethodId", e.target.value)}
                >
                  <option value="">Não informada</option>
                  {options.data?.paymentMethods.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </Field>
              {form.issueInvoice && (
                <Field label="Natureza de operação" className="col-12">
                  <select
                    className="form-select"
                    value={form.taxOperationId}
                    onChange={(e) => set("taxOperationId", e.target.value)}
                  >
                    <option value="">Do cliente ou a padrão</option>
                    {options.data?.taxOperations.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.isDefault ? " (padrão)" : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Observações" className="col-12">
                <textarea
                  rows={2}
                  maxLength={500}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                />
              </Field>
            </div>
            {problem && group && <p className="text-danger small">{problem}</p>}
            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={!group || !!problem || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Faturando…" : "Faturar"}
            </button>
          </div>
        </aside>
      </div>
    </>
  );
}
